import { useEffect, useMemo, useState, useRef, useCallback, useLayoutEffect, Component } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useHubEvent, useHubPoll, useVisibilityRefetch, useDebouncedCallback } from "@/lib/hubSync";
import { getSocket } from "@/lib/socket";
import { HUB_FEATURE_FLAGS } from "@/lib/hubFeatureFlags";
import HubSidebar from "../components/hub/HubSidebar";
import HubOnboarding from "@/components/hub/onboarding/HubOnboarding";
import { clearPendingInvite, discordLoginUrl } from "@/lib/pendingInvite";
import "./HubPage.css";

const hub = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// After ANY successful `hub/auth/logout` call the shared WebSocket must
// pivot identity so it re-derives from the (now missing / new) cookie.
// Doing it once here beats sprinkling `resetIdentity()` across every
// per-page logout button.
hub.interceptors.response.use((resp) => {
  try {
    const url = String(resp.config?.url || "");
    if (/\/hub\/auth\/logout$/.test(url) && resp.status < 400) {
      const s = getSocket();
      try { s.resetIdentity(); } catch { /* noop */ }
      try { s.reconnect(); } catch { /* noop */ }
    }
  } catch { /* never break the app for a socket pivot failure */ }
  return resp;
});

const PLATFORMS = [
  { id: "", label: "All" },
  { id: "x", label: "X Post" },
  { id: "instagram", label: "Instagram Post" },
  { id: "tiktok", label: "TikTok Post" },
];

const PLAT_META = {
  x: { name: "X POST", icon: "𝕏", color: "#e8edf7" },
  instagram: { name: "INSTAGRAM POST", icon: "📷", color: "#e879f9" },
  tiktok: { name: "TIKTOK POST", icon: "♪", color: "#22d3ee" },
};

const RING_HUES = [260, 210, 320, 190, 25, 285, 355, 165, 45, 300];

function ringFor(userId) {
  let h = 0; for (let i = 0; i < (userId || "").length; i++) h = (h * 31 + userId.charCodeAt(i)) & 0xffff;
  return `hsl(${RING_HUES[h % RING_HUES.length]}, 70%, 55%)`;
}

function fmtTimeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime(); const d = Date.now() - t;
  const m = Math.round(d / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h`;
  const days = Math.round(h / 24); return `${days}d`;
}

/* ------------------------------------------------------------------
 * Social embed lifecycle — post-mortem fix
 * ------------------------------------------------------------------
 * Root cause of the earlier `removeChild NotFoundError` crash:
 *   - A 6s timeout could set state=failed AFTER a successful embed,
 *     causing React to reconcile the same JSX slot from a mutated
 *     third-party subtree (Twitter/IG iframe) to a plain <FallbackCard>.
 *   - Third-party scripts had already moved/replaced children React
 *     thought it owned, so React's removeChild pointed at a stale node.
 *
 * Fixes applied (matches the client's 13-point spec):
 *   1) The embed lifecycle no longer swaps DOM slots. React owns an
 *      OUTER wrapper. The INNER container is populated imperatively —
 *      React never renders JSX children inside it, so it never
 *      reconciles what the platform script mutates.
 *   2) Loading overlay + fallback message live as SIBLINGS of that
 *      isolated container, never in the same slot.
 *   3) Platform scripts are loaded exactly once (memoized promise per
 *      platform).
 *   4) A `data-embed-initialized` flag protects against StrictMode
 *      double-invocation, feed refetches, filter changes, etc.
 *   5) A MutationObserver marks the embed OK the moment an <iframe>
 *      appears (real render signal, not a timing heuristic).
 *   6) A generous 18s hard-fail timer only marks failed if the
 *      container is still genuinely empty; it can never override
 *      an already-rendered embed.
 *   7) An <EmbedErrorBoundary> around each card catches any other
 *      third-party misbehaviour so one bad post can't crash the feed.
 * ------------------------------------------------------------------ */

const _platformScriptPromises = {};
function loadPlatformScript(platform) {
  const cfg = {
    x: { src: "https://platform.twitter.com/widgets.js" },
    instagram: { src: "https://www.instagram.com/embed.js" },
    tiktok: { src: "https://www.tiktok.com/embed.js" },
  }[platform];
  if (!cfg) return Promise.resolve();
  if (_platformScriptPromises[platform]) return _platformScriptPromises[platform];
  _platformScriptPromises[platform] = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${cfg.src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => resolve());
      // Already-loaded scripts fire no events; assume ready next tick.
      setTimeout(() => resolve(), 0);
      return;
    }
    const s = document.createElement("script");
    s.async = true; s.src = cfg.src; s.crossOrigin = "anonymous";
    s.onload = () => resolve();
    s.onerror = () => resolve();       // never reject — script failure is expected and safe
    document.body.appendChild(s);
  });
  return _platformScriptPromises[platform];
}

class EmbedErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(err) {
    // Never rethrow — one bad embed must not kill the feed.
    if (typeof console !== "undefined") console.warn("[hub embed]", err?.message || err);
  }
  render() {
    if (this.state.crashed) return <FallbackCard item={this.props.item} />;
    return this.props.children;
  }
}

function PostEmbed({ item }) {
  // Lazy-load gate. The heavy embed pipeline (script load + iframe spawn)
  // only kicks in once the card is within the preload margin. This runs
  // in two steps to work reliably with the masonry/grid layout:
  //   1. A synchronous rect check on the first frame — any card already
  //      inside "viewport + preload" activates immediately, without
  //      waiting for the IntersectionObserver to deliver its first
  //      callback. This is the fix for masonry columns where the
  //      observer's initial callback may miss cards whose final layout
  //      position lands mid-scroll after fonts/images finish sizing.
  //   2. If the card is NOT yet near view, wire an IntersectionObserver
  //      with a generous ~1 viewport preload on top + ~1.2 viewport
  //      preload on bottom so upcoming cards start loading before the
  //      user scrolls to them.
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return undefined;
    const node = wrapperRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    // Preload buffers — the observer AND the synchronous check use the
    // same values so on-screen cards always activate on first frame and
    // near-viewport cards preload eagerly.
    const PRELOAD_TOP = 1000;
    const PRELOAD_BOTTOM = 1200;
    const PRELOAD_HORIZONTAL = 200;
    const checkNow = () => {
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const vw = window.innerWidth  || document.documentElement.clientWidth  || 0;
      const inView =
        rect.bottom >= -PRELOAD_TOP &&
        rect.top    <= vh + PRELOAD_BOTTOM &&
        rect.right  >= -PRELOAD_HORIZONTAL &&
        rect.left   <= vw + PRELOAD_HORIZONTAL;
      if (inView) { setVisible(true); return true; }
      return false;
    };
    // Step 1: immediate synchronous check. Any card already on-screen
    // (including deep columns of a 4-column masonry at 1920×1080) starts
    // loading right now, in the same task the effect runs.
    if (checkNow()) return undefined;
    // Step 2: also re-check after the next animation frame — protects
    // against layout jitter from masonry / late-arriving fonts / images
    // when the card's final position isn't yet stable.
    const raf = requestAnimationFrame(() => { checkNow(); });
    // Step 3: observer for anything not yet in the preload window.
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    }, {
      rootMargin: `${PRELOAD_TOP}px ${PRELOAD_HORIZONTAL}px ${PRELOAD_BOTTOM}px ${PRELOAD_HORIZONTAL}px`,
      threshold: 0.01,
    });
    io.observe(node);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [visible]);
  return (
    <div ref={wrapperRef} className="hub-embed-wrap" data-testid={`embed-wrap-${item.submission_id}`}>
      {visible ? <PostEmbedInner item={item} /> : <EmbedSkeleton item={item} />}
    </div>
  );
}

function EmbedSkeleton({ item }) {
  // Same "LOADING PREVIEW" overlay used by PostEmbedInner while waiting
  // for the mutation observer — keeps the visual identical whether the
  // card is off-screen (waiting to lazy-load) or visible (loading).
  return (
    <div className="hub-embed hub-embed--loading" data-platform={item.platform}
         data-testid={`embed-skeleton-${item.submission_id}`}>
      <PlaceholderVisual platform={item.platform} />
    </div>
  );
}

/**
 * PlaceholderVisual — dark preview stand-in shown while a social embed
 * script is loading (or when it fails altogether). Fills the card so
 * the transmission never looks empty. Big white platform logo, subtle
 * cyan/violet glow ring, slow light pulse.
 */
function PlaceholderVisual({ platform }) {
  return (
    <div className="hub-placeholder"
         data-platform={platform || "x"}
         data-testid={`embed-placeholder-${platform || "unknown"}`}
         aria-hidden="true">
      <div className="hub-placeholder-glow" />
      <div className="hub-placeholder-logo">
        <PlatformGlyph platform={platform} />
      </div>
    </div>
  );
}

function PlatformGlyph({ platform }) {
  if (platform === "instagram") {
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Instagram">
        <path fill="currentColor" d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.22.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.05.42 2.22.06 1.26.07 1.64.07 4.85s0 3.6-.07 4.85c-.05 1.17-.25 1.8-.42 2.22-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.05.37-2.22.42-1.26.06-1.64.07-4.85.07s-3.6 0-4.85-.07c-1.17-.05-1.8-.25-2.22-.42a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.17-.42-.37-1.05-.42-2.22C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.85c.05-1.17.25-1.8.42-2.22.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.05-.37 2.22-.42C8.4 2.2 8.8 2.2 12 2.2Zm0 1.98c-3.15 0-3.52 0-4.76.06-1.07.05-1.65.23-2.03.38-.51.2-.87.44-1.25.82-.38.38-.62.74-.82 1.25-.15.38-.33.96-.38 2.03-.06 1.24-.06 1.61-.06 4.76s0 3.52.06 4.76c.05 1.07.23 1.65.38 2.03.2.51.44.87.82 1.25.38.38.74.62 1.25.82.38.15.96.33 2.03.38 1.24.06 1.61.06 4.76.06s3.52 0 4.76-.06c1.07-.05 1.65-.23 2.03-.38.51-.2.87-.44 1.25-.82.38-.38.62-.74.82-1.25.15-.38.33-.96.38-2.03.06-1.24.06-1.61.06-4.76s0-3.52-.06-4.76c-.05-1.07-.23-1.65-.38-2.03a3.36 3.36 0 0 0-.82-1.25 3.36 3.36 0 0 0-1.25-.82c-.38-.15-.96-.33-2.03-.38-1.24-.06-1.61-.06-4.76-.06Zm0 3.36a4.46 4.46 0 1 1 0 8.92 4.46 4.46 0 0 1 0-8.92Zm0 1.98a2.48 2.48 0 1 0 0 4.96 2.48 2.48 0 0 0 0-4.96Zm5.66-2.2a1.04 1.04 0 1 1-2.08 0 1.04 1.04 0 0 1 2.08 0Z"/>
      </svg>
    );
  }
  if (platform === "tiktok") {
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="TikTok">
        <path fill="currentColor" d="M14.3 2h2.9c.19 1.42.86 2.7 1.9 3.66 1.03.94 2.35 1.52 3.8 1.62v3.03c-1.63-.05-3.2-.52-4.58-1.36v6.5a6.4 6.4 0 1 1-6.4-6.4c.36 0 .72.03 1.07.1v3.16a3.3 3.3 0 1 0 2.31 3.14V2Z"/>
      </svg>
    );
  }
  // default: X (Twitter)
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="X">
      <path fill="currentColor" d="M18.244 2H21.5l-7.53 8.61L22.75 22h-6.86l-5.37-6.86L4.24 22H.98l8.05-9.2L1.25 2h7.03l4.85 6.28L18.24 2Zm-1.2 18.02h1.9L7.05 3.88H5.02l12.02 16.14Z"/>
    </svg>
  );
}

function PostEmbedInner({ item }) {
  const containerRef = useRef(null);
  // Ref-driven state to avoid rerendering during the platform script's async work.
  // We still call setLocal() to trigger overlay/fallback visibility toggles, but
  // those are on SIBLING elements React fully owns.
  const [status, setStatus] = useState("loading");        // "loading" | "ok" | "failed"

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    // StrictMode + feed re-renders can invoke this effect twice for the same
    // (platform, external_post_id). The dataset flag makes the imperative
    // population strictly idempotent.
    if (node.dataset.embedInitialized === "1") return undefined;
    node.dataset.embedInitialized = "1";

    let cancelled = false;
    let observer = null;
    let hardFailTimer = null;
    const generation = (node.dataset.embedGen = String((Date.now() ^ Math.random() * 1e9) | 0));

    const isStillCurrent = () => !cancelled && node.dataset.embedGen === generation;
    const setOk = () => { if (isStillCurrent()) setStatus("ok"); };
    const setFail = () => { if (isStillCurrent()) setStatus("failed"); };
    const disconnectObserver = () => { if (observer) { try { observer.disconnect(); } catch (_) { /* noop */ } observer = null; } };
    const clearHardTimer = () => { if (hardFailTimer) { clearTimeout(hardFailTimer); hardFailTimer = null; } };

    // A real render always produces an <iframe> for X/IG/TikTok. Watch for one.
    observer = new MutationObserver(() => {
      if (!isStillCurrent()) return;
      if (node.querySelector("iframe")) {
        setOk();
        disconnectObserver();
        clearHardTimer();
      }
    });
    observer.observe(node, { childList: true, subtree: true });

    // Populate the container imperatively. React never sees these children.
    try {
      if (item.platform === "x") {
        loadPlatformScript("x").then(() => {
          if (!isStillCurrent()) return;
          const w = window.twttr && window.twttr.widgets;
          if (!w || typeof w.createTweet !== "function") { setFail(); return; }
          w.createTweet(item.external_post_id, node, { theme: "dark", dnt: true, align: "center" })
            .then((el) => { if (!isStillCurrent()) return; if (el) setOk(); else setFail(); })
            .catch(() => setFail());
        });
      } else if (item.platform === "instagram") {
        const bq = document.createElement("blockquote");
        bq.className = "instagram-media";
        bq.setAttribute("data-instgrm-permalink", item.canonical_url);
        bq.setAttribute("data-instgrm-version", "14");
        bq.style.background = "#0a0e18"; bq.style.border = "0"; bq.style.margin = "0";
        bq.style.minWidth = "0"; bq.style.width = "100%";
        node.appendChild(bq);
        loadPlatformScript("instagram").then(() => {
          if (!isStillCurrent()) return;
          try { window.instgrm && window.instgrm.Embeds && window.instgrm.Embeds.process(); } catch (_) { /* noop */ }
        });
      } else if (item.platform === "tiktok") {
        const bq = document.createElement("blockquote");
        bq.className = "tiktok-embed";
        bq.setAttribute("cite", item.canonical_url);
        bq.setAttribute("data-video-id", item.external_post_id);
        bq.style.maxWidth = "100%"; bq.style.background = "#0a0e18";
        bq.appendChild(document.createElement("section"));
        node.appendChild(bq);
        // TikTok's script auto-scans for `.tiktok-embed` blockquotes on load.
        loadPlatformScript("tiktok");
      } else {
        setFail();
      }
    } catch (e) {
      setFail();
    }

    // Platform-specific hard-fail timeouts. Instagram's embed.js can
    // silently fail (returns without inserting an iframe) on many
    // production origins — giving up faster shows the polished fallback
    // sooner and stops the "empty black loading" perception.
    const HARD_FAIL_MS =
      item.platform === "instagram" ? 7000
      : item.platform === "tiktok"  ? 12000
      : 18000;
    hardFailTimer = setTimeout(() => {
      if (!isStillCurrent()) return;
      if (!node.querySelector("iframe")) setFail();
    }, HARD_FAIL_MS);

    return () => {
      cancelled = true;
      disconnectObserver();
      clearHardTimer();
      // Safe imperative teardown — React never rendered these children, so it
      // never expected to remove them. Bump generation so any late callbacks
      // from the platform's promises are ignored.
      try { node.dataset.embedGen = ""; node.dataset.embedInitialized = ""; } catch (_) { /* noop */ }
      try { while (node.firstChild) node.removeChild(node.firstChild); } catch (_) { /* noop */ }
    };
  }, [item.platform, item.external_post_id, item.canonical_url]);

  return (
    <div className="hub-embed-wrapper" data-testid={`embed-wrapper-${item.submission_id}`}>
      {/* React OWNS this outer div. The inner container is imperatively managed. */}
      <div ref={containerRef} className="hub-embed-inner"
           style={{ visibility: status === "failed" ? "hidden" : "visible" }}
           aria-hidden={status === "failed"} />
      {/* Dark placeholder — visible until the real embed's <iframe>
          appears (mutation observer flips status to "ok"), then fades
          out smoothly. On "failed", stays visible; the card footer's
          existing "VIEW ON X" button preserves access to the source. */}
      <div className={`hub-embed-placeholder-slot${status === "ok" ? " is-hidden" : ""}${status === "failed" ? " is-failed" : ""}`}>
        <PlaceholderVisual platform={item.platform} />
      </div>
    </div>
  );
}

/**
 * Fire-and-forget tracker for admin-only Transmission-open telemetry.
 * The user's actual click on the external link happens synchronously in
 * the anchor's default behavior; this POST goes out in parallel and MUST
 * NOT block, cancel, or await. If it fails, the link still opens.
 *
 * Auth is derived from the httpOnly `hub_session` cookie server-side.
 * The frontend never sends a user_id — a forged one would be ignored.
 */
function trackTransmissionOpen(submissionId) {
  if (!submissionId) return;
  // withCredentials on `hub` sends the session cookie. We don't await —
  // any network / server error is silently ignored so the anchor's
  // navigation is never disturbed by tracking.
  try {
    hub.post(`/hub/transmissions/${submissionId}/open`, {}).catch(() => { /* noop */ });
  } catch { /* noop */ }
}

function FallbackCard({ item }) {
  const meta = PLAT_META[item.platform] || {};
  const platformLabel =
    item.platform === "x" ? "View on X"
    : item.platform === "instagram" ? "View on Instagram"
    : item.platform === "tiktok" ? "View on TikTok"
    : "View Original Post";
  const author = item.author || {};
  // Instagram-only: enrich with a real thumbnail + author username when
  // the backend can obtain them (Meta Graph oEmbed or, historically,
  // og:image scrape). If Instagram refuses metadata, the endpoint
  // returns { ok: false } and we keep the polished icon-only card.
  const [igMeta, setIgMeta] = useState(null);
  useEffect(() => {
    if (item.platform !== "instagram") return;
    let cancelled = false;
    hub.get(`/hub/embed/instagram/${item.submission_id}`)
       .then(r => { if (!cancelled && r?.data?.ok) setIgMeta(r.data); })
       .catch(() => { /* keep the icon-only fallback */ });
    return () => { cancelled = true; };
  }, [item.submission_id, item.platform]);
  const igThumb = igMeta?.ok ? igMeta.thumbnail_url : null;
  const igAuthorHandle = igMeta?.ok ? igMeta.author_username : null;
  const igPostType = igMeta?.ok ? (igMeta.post_type || "Post") : null;
  return (
    <div className="hub-fallback" data-testid={`embed-fallback-${item.submission_id}`}
         data-platform={item.platform || ""}
         data-enriched={igThumb ? "true" : "false"}>
      {igThumb ? (
        // Enriched Instagram fallback — real thumbnail + author handle.
        <a href={item.original_url} target="_blank" rel="noopener noreferrer"
           className="hub-fallback-thumb-link"
           onClick={() => trackTransmissionOpen(item.submission_id)}
           data-testid={`ig-thumb-link-${item.submission_id}`}>
          <img src={igThumb}
               alt={igAuthorHandle
                     ? `Instagram ${igPostType || "post"} by @${igAuthorHandle}`
                     : `Instagram ${igPostType || "post"}`}
               className="hub-fallback-thumb"
               loading="lazy"
               referrerPolicy="no-referrer" />
          <div className="hub-fallback-thumb-badge" style={{ color: meta.color }}>
            {meta.icon} <span>{igPostType || "Post"}</span>
          </div>
        </a>
      ) : (
        <div className="hub-fallback-icon" style={{ color: meta.color }}>{meta.icon}</div>
      )}
      {(igAuthorHandle || author.display_name) && (
        <div className="hub-fallback-author">
          {igAuthorHandle
            ? <span className="hub-fallback-author-name">@{igAuthorHandle}</span>
            : (
                <>
                  <span className="hub-avatar hub-avatar--sm"
                        style={{ boxShadow: `0 0 0 2px ${ringFor(author.user_id)}` }}>
                    <img src={author.avatar_url} alt="" />
                  </span>
                  <span className="hub-fallback-author-name">{author.display_name}</span>
                </>
              )}
        </div>
      )}
      {!igThumb && (
        <div className="hub-fallback-title">
          {meta.name || "Post"} preview is unavailable in this browser.
        </div>
      )}
      <a href={item.original_url} target="_blank" rel="noopener noreferrer"
         className="hub-btn hub-btn--ghost"
         onClick={() => trackTransmissionOpen(item.submission_id)}
         data-testid={`view-original-fallback-${item.submission_id}`}>
        {platformLabel} ↗
      </a>
    </div>
  );
}

function PostCard({ item }) {
  const meta = PLAT_META[item.platform] || {};
  const author = item.author || {};
  return (
    <article className="hub-card" data-testid={`hub-post-${item.submission_id}`}>
      <header className="hub-card-head">
        <div className="hub-card-plat">
          <span className="hub-plat-icon" style={{ color: meta.color }}>{meta.icon}</span>
          <span className="hub-plat-name">{meta.name}</span>
        </div>
        <div className="hub-card-author">
          <span className="hub-avatar" style={{ boxShadow: `0 0 0 2px ${ringFor(author.user_id)}` }}>
            <img src={author.avatar_url} alt="" />
          </span>
          <span className="hub-author-name">{author.display_name || "Prophet"}</span>
          <span className="hub-author-time">· {fmtTimeAgo(item.approved_at || item.created_at)}</span>
        </div>
      </header>
      <div className="hub-card-body"><EmbedErrorBoundary item={item}><PostEmbed item={item} /></EmbedErrorBoundary></div>
      <footer className="hub-card-foot">
        <a className="hub-btn" href={item.original_url} target="_blank" rel="noopener noreferrer"
           onClick={() => trackTransmissionOpen(item.submission_id)}
           data-testid={`view-original-${item.submission_id}`}>
          {item.platform === "x" ? "View on X" : item.platform === "instagram" ? "View on Instagram" : "View on TikTok"}
        </a>
      </footer>
    </article>
  );
}

function ActiveRow({ members, onSelect, currentUser }) {
  const bannerUrl = `${process.env.PUBLIC_URL || ""}/img/hub/banner-transmission.webp`;
  return (
    <div className="hub-active-row" data-testid="hub-active-members"
         style={{ backgroundImage: `url("${bannerUrl}")` }}>
      <div className="hub-active-title">ACTIVE TRANSMISSIONS <span className="hub-active-dot" /></div>
      <div className="hub-active-scroller">
        {currentUser ? (
          <div className="hub-active-item" onClick={() => onSelect?.(currentUser.user_id)}>
            <span className="hub-avatar hub-avatar--lg" style={{ boxShadow: `0 0 0 3px ${ringFor(currentUser.user_id)}` }}>
              <img src={currentUser.avatar_url} alt="" />
            </span>
            <span className="hub-active-name">You</span>
          </div>
        ) : null}
        {members.map((m) => (
          <div key={m.user_id} className="hub-active-item" onClick={() => onSelect?.(m.user_id)}
               data-testid={`active-member-${m.user_id}`}>
            <span className="hub-avatar hub-avatar--lg" style={{ boxShadow: `0 0 0 3px ${ringFor(m.user_id)}` }}>
              <img src={m.avatar_url} alt="" />
            </span>
            <span className="hub-active-name">{m.display_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectDiscordButton({ compact = false }) {
  // Phase 2 — include the pending invitation from sessionStorage so a
  // visitor who navigated away from `/hub/invite/:code` before signing
  // in still lands on the invitation admission path.
  const href = discordLoginUrl();
  return (
    <a href={href} className={`hub-btn hub-btn--discord${compact ? " hub-btn--compact" : ""}`}
       data-testid="hub-connect-discord">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3a13.29 13.29 0 0 0-.62 1.26 18.28 18.28 0 0 0-5.876 0A13.35 13.35 0 0 0 9.443 3a19.79 19.79 0 0 0-3.76 1.369C2.146 9.7 1.32 14.9 1.66 20.02a19.86 19.86 0 0 0 6.032 3.06c.487-.66.919-1.363 1.29-2.1a12.99 12.99 0 0 1-2.03-.98c.17-.126.335-.257.494-.39a14.15 14.15 0 0 0 12.11 0c.16.133.324.264.494.39-.647.383-1.327.716-2.03.98.37.737.803 1.44 1.29 2.1a19.83 19.83 0 0 0 6.032-3.06c.42-5.87-.72-11.02-3.024-15.65Z"/>
      </svg>
      CONNECT DISCORD
    </a>
  );
}

function UserChip({ me, onLogout, onMy, onProfile, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Close on outside click / tap so the mobile dropdown behaves as
  // expected on touch devices (a background tap dismisses it).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);
  // On mobile (compact) the avatar is a direct link to the profile
  // page — no dropdown. Logout has been relocated to the bottom of
  // the mobile navigation drawer, so the compact chip no longer needs
  // to expose it. Desktop keeps the full dropdown behaviour below.
  if (compact) {
    return (
      <div className="hub-userchip hub-userchip--compact" data-testid="hub-userchip">
        <button type="button"
                className="hub-userchip-btn hub-userchip-btn--profile"
                onClick={() => onProfile?.()}
                aria-label="Go to profile"
                data-testid="hub-userchip-profile">
          <span className="hub-avatar" style={{ boxShadow: `0 0 0 2px ${ringFor(me.user_id)}` }}>
            <img src={me.avatar_url} alt="" />
          </span>
          <span className="hub-userchip-name">{me.display_name}</span>
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} className="hub-userchip" data-testid="hub-userchip">
      <button className="hub-userchip-btn" onClick={() => setOpen((v) => !v)}
              aria-expanded={open ? "true" : "false"}>
        <span className="hub-avatar" style={{ boxShadow: `0 0 0 2px ${ringFor(me.user_id)}` }}>
          <img src={me.avatar_url} alt="" />
        </span>
        <span className="hub-userchip-name">{me.display_name}</span>
        <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 3l3 4 3-4" stroke="currentColor" fill="none" strokeWidth="1.5"/></svg>
      </button>
      {open && (
        <div className="hub-userchip-menu"
             role="menu">
          <button onClick={() => { setOpen(false); onProfile?.(); }} data-testid="hub-menu-profile">
            {HUB_FEATURE_FLAGS.HUB_NEW_NAV ? "View Profile" : "My Profile"}
          </button>
          {HUB_FEATURE_FLAGS.HUB_NEW_NAV ? (
            <button onClick={() => { setOpen(false); onProfile?.(); }} data-testid="hub-menu-account">Account</button>
          ) : (
            <button onClick={() => { setOpen(false); onMy?.(); }} data-testid="hub-menu-my">My Transmissions</button>
          )}
          <button onClick={() => { setOpen(false); onLogout?.(); }}
                  data-testid="hub-menu-logout">
            Log Out
          </button>
        </div>
      )}
    </div>
  );
}

function SubmitModal({ open, onClose, onSubmitted }) {
  const [url, setUrl] = useState(""); const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [okId, setOkId] = useState("");
  useEffect(() => { if (!open) { setUrl(""); setNote(""); setErr(""); setOkId(""); setBusy(false); } }, [open]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await hub.post("/hub/submissions", { url, note });
      setOkId(data.submission_id); onSubmitted?.();
    } catch (er) {
      setErr(er?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="hub-modal-root" role="dialog" aria-modal="true" data-testid="hub-submit-modal">
      <div className="hub-modal-backdrop" onClick={onClose} />
      <div className="hub-modal-panel">
        <button className="hub-modal-close" onClick={onClose} aria-label="Close">×</button>
        {okId ? (
          <div className="hub-modal-ok" data-testid="hub-submit-ok">
            <h3>TRANSMISSION RECEIVED</h3>
            <p>Your submission will appear in the Prophets Hub once approved.</p>
            <button onClick={onClose} className="hub-btn">Close</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3>Submit a Transmission</h3>
            <p className="hub-modal-sub">Paste a link to your X, Instagram or TikTok post about Prophets.</p>
            <label>Social post URL
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://x.com/…"
                     required data-testid="hub-submit-url" />
            </label>
            <label>Note to admin <span className="hub-optional">(optional)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
                        data-testid="hub-submit-note" />
            </label>
            {err && <div className="hub-modal-err" data-testid="hub-submit-err">{err}</div>}
            <button type="submit" disabled={busy || !url.trim()} className="hub-btn hub-btn--primary"
                    data-testid="hub-submit-btn">
              {busy ? "Submitting…" : "Submit for Review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function MyTransmissions({ open, onClose }) {
  const [tab, setTab] = useState("pending"); const [items, setItems] = useState([]);
  const load = useCallback(async (t) => {
    const { data } = await hub.get(`/hub/my-submissions?status=${t}`);
    setItems(data.items || []);
  }, []);
  useEffect(() => { if (open) load(tab); }, [open, tab, load]);
  if (!open) return null;
  return (
    <div className="hub-modal-root" role="dialog" aria-modal="true" data-testid="hub-my-transmissions">
      <div className="hub-modal-backdrop" onClick={onClose} />
      <div className="hub-modal-panel hub-modal-panel--wide">
        <button className="hub-modal-close" onClick={onClose}>×</button>
        <h3>My Transmissions</h3>
        <div className="hub-my-tabs">
          {["pending", "approved", "rejected"].map((t) => (
            <button key={t} className={tab === t ? "is-active" : ""} onClick={() => setTab(t)}
                    data-testid={`hub-my-tab-${t}`}>{t}</button>
          ))}
        </div>
        <div className="hub-my-list">
          {items.length === 0 ? <p className="hub-empty">No {tab} submissions.</p> :
            items.map((s) => (
              <div key={s.submission_id} className="hub-my-row" data-testid={`hub-my-row-${s.submission_id}`}>
                <span className="hub-plat-icon">{PLAT_META[s.platform]?.icon}</span>
                <a href={s.original_url} target="_blank" rel="noopener noreferrer">{s.canonical_url}</a>
                <span className="hub-my-status" data-status={s.status}>{s.status}</span>
                {s.rejection_reason && <div className="hub-my-reason">Reason: {s.rejection_reason}</div>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * HubHeader — /hub-only navigation. Deliberately does NOT reuse
 * ProphetsHeader: the main site nav (Collection / About / Utility / Games /
 * Lore / The Oracle / Hub / Mint / socials) is intentionally hidden here,
 * per spec. The main landing page and every other route still render the
 * full ProphetsHeader untouched.
 * ------------------------------------------------------------------------- */
// Mobile navigation entries. Kept in sync with the desktop sidebar's
// `HUB_NAV_ITEMS` (see `/components/hub/HubSidebar.jsx`) — adding a
// destination on desktop MUST also add it here, otherwise mobile
// users lose access to it. When adding a new nav row on desktop,
// append the matching entry below in the same relative position and
// preserve the same `key` so `active` matching keeps working.
const HUB_MENU_ITEMS = HUB_FEATURE_FLAGS.HUB_NEW_NAV
  ? [
      { to: "/hub",              key: "home",          label: "HOME" },
      { to: "/hub/transmissions", key: "transmissions", label: "TRANSMISSIONS", group: "earn" },
      ...(HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED
        ? [{ to: "/hub/quiz",     key: "quiz",          label: "QUIZ", group: "earn" }]
        : []),
      { to: "/hub/daily-spin",   key: "daily-spin",    label: "DAILY SPIN", group: "earn" },
      ...(HUB_FEATURE_FLAGS.HUB_PROPHECY_ENABLED
        ? [{ to: "/hub/prophecy", key: "prophecy", label: "PROPHECY", group: "earn" }]
        : []),
      { to: "/hub/predictions",  key: "predictions",   label: "PREDICTIONS" },
      { to: "/hub/marketplace",  key: "marketplace",   label: "MARKETPLACE" },
      { to: "/hub/orbis",        key: "orbis",         label: "ORBIS" },
      { to: "/hub/games",        key: "games",         label: "GAMES" },
      { to: "/hub/family-race",  key: "family-race",   label: "FAMILY RACE" },
      // Headquarters — same label, key and relative position as the
      // desktop sidebar (between Family Race and THE SWEEP).
      // Individual family Headquarters pages
      // (`/hub/headquarters/:familyKey`) also flag
      // `active="headquarters"`, so this row keeps the correct
      // active state on the directory AND on each family page.
      // Access to gated family HQs is still enforced entirely
      // server-side — this menu only points at the directory,
      // which itself only offers ENTER HQ on families the user
      // holds.
      { to: "/hub/headquarters", key: "headquarters",  label: "HEADQUARTERS" },
      // THE SWEEP — public event page. Sits directly between
      // HEADQUARTERS and ZODIAC on both desktop + mobile so the
      // primary event modules cluster together. Access is
      // standard Hub session; the public page itself renders
      // "no event" when nothing is active.
      { to: "/hub/sweep",        key: "sweep",         label: "THE SWEEP" },
      { to: "/hub/zodiac",       key: "zodiac",        label: "ZODIAC" },
      // Golden Vault — mirrors the desktop sidebar entry (same label,
      // same key, same relative position at the very bottom of the
      // module list, just above the profile pair). Access to the
      // interior chamber remains server-side gated; this menu row
      // only points at the public exterior entrance.
      { to: "/hub/golden-vault", key: "golden-vault",  label: "GOLDEN VAULT" },
      // HOLD & EARN — mirrors the desktop sidebar entry.
      { to: "/hub/hold-and-earn", key: "hold-and-earn", label: "HOLD & EARN" },
      // CASINO — hidden while HUB_CASINO_ENABLED is false (unfinished).
      // Kept here so desktop/mobile parity is restored by one flag flip.
      ...(HUB_FEATURE_FLAGS.HUB_CASINO_ENABLED
        ? [{ to: "/hub/casino",  key: "casino",        label: "CASINO" }]
        : []),
      { to: "/hub/profile",      key: "profile",       label: "PROFILE", group: "profile" },
      { to: "/hub/portfolio",    key: "portfolio",     label: "MY PORTFOLIO" },
    ]
  : [
      { to: "/hub",             key: "transmissions", label: "TRANSMISSIONS" },
      ...(HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED
        ? [{ to: "/hub/quiz",    key: "quiz",          label: "QUIZ" }]
        : []),
      { to: "/hub/daily-spin",  key: "daily-spin",    label: "DAILY SPIN" },
      ...(HUB_FEATURE_FLAGS.HUB_PROPHECY_ENABLED
        ? [{ to: "/hub/prophecy",    key: "prophecy",      label: "PROPHECY" }]
        : []),
      { to: "/hub/marketplace", key: "marketplace",   label: "MARKETPLACE" },
      { to: "/hub/headquarters", key: "headquarters", label: "HEADQUARTERS" },
      { to: "/hub/my-predictions", key: "my-predictions", label: "MY PREDICTIONS" },
      { to: "/hub/profile",     key: "profile",       label: "MY PROFILE" },
    ];

function HubMobileMenu({ active, open, onClose, onLogout }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while overlay is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  // Portal to document.body so an ancestor transform/filter on the Hub
  // shell can't constrain our position:fixed overlay to the header row.
  return createPortal(
    <div className="hub-mnav-overlay" data-testid="hub-mobile-menu"
         role="dialog" aria-modal="true" aria-label="Hub navigation">
      <button type="button"
              className="hub-mnav-backdrop"
              aria-label="Close menu"
              onClick={onClose}
              data-testid="hub-mobile-menu-backdrop" />
      <nav className="hub-mnav-panel">
        <ul className="hub-mnav-list">
          {HUB_MENU_ITEMS.map(item => (
            <li key={item.key}>
              <Link to={item.to}
                    onClick={onClose}
                    className={`hub-mnav-item${active === item.key ? " is-active" : ""}`}
                    aria-current={active === item.key ? "page" : undefined}
                    data-testid={`hub-mobile-menu-${item.key}`}>
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        {/* Standalone Logout action pinned to the bottom of the mobile
            drawer. Visually distinct (cyan accent) from the nav items
            so it doesn't read as another destination. Uses the shared
            logout flow. */}
        {onLogout && (
          <button type="button"
                  className="hub-mnav-logout"
                  onClick={() => { onClose(); onLogout(); }}
                  data-testid="hub-mobile-menu-logout">
            LOGOUT
          </button>
        )}
      </nav>
    </div>,
    document.body,
  );
}

function EarnMenu({ active }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const inEarn = ["transmissions", "quiz", "daily-spin", "prophecy"].includes(active);
  return (
    <div ref={wrap} className="hub-nav-dd" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {/* Slice S9 — the label is a real link that navigates to the EARN
          default subsection (Transmissions). Hover still opens the
          dropdown shortcut. Chevron uses aria-hidden so screen readers
          treat this as one navigation link. */}
      <Link to="/hub/transmissions"
            className={`hub-header-tab${inEarn ? " is-active" : ""}`}
            aria-haspopup="menu" aria-expanded={open ? "true" : "false"}
            aria-current={inEarn ? "page" : undefined}
            data-testid="hub-tab-earn"
            onClick={() => setOpen(false)}>
        EARN <span aria-hidden="true" className="hub-tab-caret">▾</span>
      </Link>
      {open && (
        <div className="hub-nav-dd-panel" role="menu" data-testid="hub-earn-menu">
          <Link to="/hub/transmissions" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-earn-transmissions">TRANSMISSIONS</Link>
          {HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED && (
            <Link to="/hub/quiz" className="hub-nav-dd-item" role="menuitem"
                  onClick={() => setOpen(false)} data-testid="hub-earn-quiz">QUIZ</Link>
          )}
          <Link to="/hub/daily-spin" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-earn-daily-spin">DAILY SPIN</Link>
          {HUB_FEATURE_FLAGS.HUB_PROPHECY_ENABLED && (
            <Link to="/hub/prophecy" className="hub-nav-dd-item" role="menuitem"
                  onClick={() => setOpen(false)} data-testid="hub-earn-prophecy">PROPHECY</Link>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ active }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const inProfile = ["profile", "profile-dashboard", "profile-public", "profile-account"].includes(active);
  return (
    <div ref={wrap} className="hub-nav-dd" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {/* Slice S9 — clicking the PROFILE label goes directly to the
          Dashboard sub-tab. Hover keeps the dropdown shortcut. */}
      <Link to="/hub/profile?tab=dashboard"
            className={`hub-header-tab${inProfile ? " is-active" : ""}`}
            aria-haspopup="menu" aria-expanded={open ? "true" : "false"}
            aria-current={inProfile ? "page" : undefined}
            data-testid="hub-tab-profile"
            onClick={() => setOpen(false)}>
        PROFILE <span aria-hidden="true" className="hub-tab-caret">▾</span>
      </Link>
      {open && (
        <div className="hub-nav-dd-panel" role="menu" data-testid="hub-profile-menu">
          <Link to="/hub/profile?tab=dashboard" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-profile-dashboard">DASHBOARD</Link>
          <Link to="/hub/profile" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-profile-overview">OVERVIEW</Link>
          <Link to="/hub/profile?tab=public" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-profile-public">PUBLIC PROFILE</Link>
          <Link to="/hub/profile?tab=account" className="hub-nav-dd-item" role="menuitem"
                onClick={() => setOpen(false)} data-testid="hub-profile-account">ACCOUNT</Link>
        </div>
      )}
    </div>
  );
}

function HubHeader({ me, onMy, onProfile, onLogout, active = "transmissions" }) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // ---- Slice S11: desktop sidebar (persists collapsed state via
  // localStorage). Toggle a body class so the shell/header CSS can
  // reserve the sidebar's column width. Only touches body on desktop
  // widths — the media queries in HubSidebar.css guard mobile.
  //
  // IMPORTANT — first-paint contract:
  //   Uses `useLayoutEffect` (not `useEffect`) so the `hub-sb-active`
  //   class is on `<body>` BEFORE the browser paints this component.
  //   Combined with the JSX-level guard below (which omits the
  //   horizontal tab bar entirely when HUB_NEW_NAV is on) this
  //   prevents the "wrong layout flash" that used to appear for one
  //   frame between HubBootLoader unmounting and the sidebar CSS
  //   taking effect.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("hub-sb-active");
    const sync = () => {
      const collapsed = (() => {
        try { return window.localStorage.getItem("hub.sidebar.collapsed") === "1"; }
        catch { return false; }
      })();
      document.body.classList.toggle("hub-sb-active--collapsed", collapsed);
    };
    sync();
    window.addEventListener("storage", sync);
    // The sidebar itself dispatches this event on every toggle so
    // pages already loaded pick up the new column width without a
    // re-render loop through parent props.
    window.addEventListener("hub-sidebar:toggle", sync);
    return () => {
      document.body.classList.remove("hub-sb-active");
      document.body.classList.remove("hub-sb-active--collapsed");
      document.body.classList.remove("hub-sb-ready");
      window.removeEventListener("storage", sync);
      window.removeEventListener("hub-sidebar:toggle", sync);
    };
  }, []);
  // When the sidebar owns navigation (HUB_NEW_NAV=true), the header's
  // desktop-only elements are already hidden by the
  // `body.hub-sb-active .hub-header-*.hub-only-desktop { display:none }`
  // CSS rules — but we DO NOT rely on that CSS anymore: we simply
  // don't render them in JSX in the first place. This is what
  // eliminates the horizontal-nav flash on hard refresh (the CSS
  // hide only kicks in on the second paint, after the useEffect
  // that adds `hub-sb-active`; skipping the JSX makes it impossible
  // for the horizontal tabs to render at all).
  const sidebarOwnsNav = HUB_FEATURE_FLAGS.HUB_NEW_NAV;
  return (
    <>
    <HubSidebar active={active} me={me} onLogout={onLogout} />
    <header className="hub-header" data-testid="hub-header">
      <div className="hub-header-inner">
        {/* --- DESKTOP LEFT: Back to Prophets (only when the sidebar
            is not carrying navigation — otherwise CSS would hide it
            and it would just flash for one paint). --- */}
        {!sidebarOwnsNav && (
          <div className="hub-header-left hub-only-desktop">
            <Link to="/" className="hub-header-back p-nav" data-testid="hub-header-back">
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M6 2L3 5l3 3" stroke="currentColor" fill="none" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              BACK TO PROPHETS
            </Link>
          </div>
        )}
        {/* --- MOBILE LEFT: Discord identity or Connect (mobile-only) --- */}
        <div className="hub-header-left hub-only-mobile">
          {me?.authenticated ? (
            <UserChip me={me.user}
                      compact
                      onLogout={onLogout}
                      onMy={onMy || (() => navigate("/hub/profile?tab=transmissions"))}
                      onProfile={onProfile || (() => navigate("/hub/profile"))} />
          ) : (
            <ConnectDiscordButton compact />
          )}
        </div>

        {/* --- MOBILE MIDDLE: SolRadio mini-player slot (portal target)
            Rendered ONLY when authenticated, and hidden while the
            mobile navigation drawer is open. The mini card itself is
            portalled in from `PersistentSolRadio` so it shares the
            same global audio element + playback state (no competing
            audio instance is ever created). */}
        {me?.authenticated && (
          <div className="hub-mradio-slot hub-only-mobile"
               id="hub-mobile-radio-slot"
               data-menu-open={mobileOpen ? "1" : "0"}
               data-testid="hub-mobile-radio-slot"
               aria-hidden={mobileOpen ? "true" : "false"} />
        )}

        {/* --- DESKTOP CENTER: legacy horizontal Hub tabs.
            ONLY rendered when the sidebar is NOT carrying navigation
            (legacy `HUB_NEW_NAV=false` mode). When the sidebar owns
            nav we omit this <nav> entirely — this is the fix for the
            wrong-layout flash. The CSS hide rule
            (`body.hub-sb-active .hub-header-tabs.hub-only-desktop`)
            only takes effect on the second paint, after the layout
            effect adds the class; by not rendering the tabs at all
            we make that CSS rule dead code for the sidebar mode. */}
        {!sidebarOwnsNav && (
        <nav className="hub-header-tabs hub-only-desktop"
             data-testid="hub-header-tabs" aria-label="Hub sections">
          {HUB_FEATURE_FLAGS.HUB_NEW_NAV ? (
            <>
              <Link to="/hub"
                    className={`hub-header-tab${active === "home" ? " is-active" : ""}`}
                    data-testid="hub-tab-home"
                    aria-current={active === "home" ? "page" : undefined}>
                HOME
              </Link>
              <EarnMenu active={active} />
              <Link to="/hub/predictions"
                    className={`hub-header-tab${active === "predictions" ? " is-active" : ""}`}
                    data-testid="hub-tab-predictions"
                    aria-current={active === "predictions" ? "page" : undefined}>
                PREDICTIONS
              </Link>
              <Link to="/hub/marketplace"
                    className={`hub-header-tab${active === "marketplace" ? " is-active" : ""}`}
                    data-testid="hub-tab-marketplace"
                    aria-current={active === "marketplace" ? "page" : undefined}>
                MARKETPLACE
              </Link>
              <Link to="/hub/games"
                    className={`hub-header-tab${active === "games" ? " is-active" : ""}`}
                    data-testid="hub-tab-games"
                    aria-current={active === "games" ? "page" : undefined}>
                GAMES
              </Link>
              <ProfileMenu active={active} />
            </>
          ) : (
            <>
              <Link to="/hub"
                    className={`hub-header-tab${active === "transmissions" ? " is-active" : ""}`}
                    data-testid="hub-tab-transmissions"
                    aria-current={active === "transmissions" ? "page" : undefined}>
                TRANSMISSIONS
              </Link>
              <Link to="/hub/quiz"
                    className={`hub-header-tab${active === "quiz" ? " is-active" : ""}`}
                    data-testid="hub-tab-quiz"
                    aria-current={active === "quiz" ? "page" : undefined}
                    style={HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED ? undefined : { display: "none" }}>
                QUIZ
              </Link>
              <Link to="/hub/daily-spin"
                    className={`hub-header-tab${active === "daily-spin" ? " is-active" : ""}`}
                    data-testid="hub-tab-daily-spin"
                    aria-current={active === "daily-spin" ? "page" : undefined}>
                DAILY SPIN
              </Link>
              {HUB_FEATURE_FLAGS.HUB_PROPHECY_ENABLED && (
                <Link to="/hub/prophecy"
                      className={`hub-header-tab${active === "prophecy" ? " is-active" : ""}`}
                      data-testid="hub-tab-prophecy"
                      aria-current={active === "prophecy" ? "page" : undefined}>
                  PROPHECY
                </Link>
              )}
              <Link to="/hub/marketplace"
                    className={`hub-header-tab${active === "marketplace" ? " is-active" : ""}`}
                    data-testid="hub-tab-marketplace"
                    aria-current={active === "marketplace" ? "page" : undefined}>
                MARKETPLACE
              </Link>
              <Link to="/hub/my-predictions"
                    className={`hub-header-tab${active === "my-predictions" ? " is-active" : ""}`}
                    data-testid="hub-tab-my-predictions"
                    aria-current={active === "my-predictions" ? "page" : undefined}>
                MY PREDICTIONS
              </Link>
            </>
          )}
        </nav>
        )}

        {/* --- DESKTOP RIGHT: user chip / connect. Same guard as the
            legacy tabs above — when the sidebar owns navigation the
            user pill lives at the top of the sidebar itself, so this
            desktop-only header slot would just flash for one paint
            before the CSS hides it. Skip rendering it in JSX. --- */}
        {!sidebarOwnsNav && (
        <div className="hub-header-right hub-only-desktop">
          {me?.authenticated ? (
            <UserChip me={me.user}
                      onLogout={onLogout}
                      onMy={onMy || (() => navigate("/hub/profile?tab=transmissions"))}
                      onProfile={onProfile || (() => navigate("/hub/profile"))} />
          ) : (
            <ConnectDiscordButton compact />
          )}
        </div>
        )}

        {/* --- MOBILE RIGHT: hamburger --- */}
        <button type="button"
                className="hub-header-menu-btn hub-only-mobile"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen ? "true" : "false"}
                onClick={() => setMobileOpen(v => !v)}
                data-testid="hub-mobile-menu-toggle">
          <span className="hub-header-menu-icon" aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>
      </div>

      <HubMobileMenu active={active}
                     open={mobileOpen}
                     onClose={() => setMobileOpen(false)}
                     onLogout={me?.authenticated ? onLogout : undefined} />
    </header>
    </>
  );
}

export { HubHeader, UserChip, ConnectDiscordButton, hub, ringFor,
         AuthGate, HUB_AUTH_GATE_STATES, PostCard };

/* -------- Access-denied gate (guild membership) ------------------------
   Renders when the OAuth callback redirects with one of these states:
     ?auth=guild_required     — Discord user is not in the Prophets server
     ?auth=scope_denied       — user unchecked `guilds.members.read`
     ?auth=guild_check_failed — Discord API down / timeout / rate limit
     ?auth=role_required      — future holder-role gate rejected them
     ?auth=misconfigured      — DISCORD_GUILD_ID env var missing
   The gate is a top-level overlay so it also blocks any accidental in-
   flight authenticated UI (the underlying page renders in unauth mode
   anyway because the server never issued a session).
------------------------------------------------------------------------ */
const HUB_AUTH_GATE_STATES = {
  guild_required: {
    title: "PROPHETS DISCORD REQUIRED",
    body: "Join the official Prophets Discord server before accessing the Hub.",
    primary: "JOIN DISCORD",
    canRetry: true,
  },
  role_required: {
    title: "HOLDER ACCESS REQUIRED",
    body: "Prophets Hub is holder-only. Verify your Prophet ownership through Matrica in the Prophets Discord, then refresh your holder status.",
    primary: "OPEN DISCORD",
    canRetry: true,
    retryLabel: "REFRESH HOLDER STATUS",
  },
  invite_required: {
    title: "INVITATION REQUIRED",
    body: "The Prophets Hub is invitation-only for non-holders. Open the invitation link a Prophet sent you to accept your PROPHETS FRIEND admission.",
    primary: "JOIN DISCORD",
    canRetry: true,
    retryLabel: "TRY AGAIN",
  },
  scope_denied: {
    title: "PERMISSION REQUIRED",
    body: "The Hub needs to verify you are a member of the Prophets Discord. Please grant the requested permission on the next screen.",
    primary: "JOIN DISCORD",
    canRetry: true,
  },
  guild_check_failed: {
    title: "COULD NOT VERIFY MEMBERSHIP",
    body: "Discord did not respond in time. Please try again in a moment.",
    primary: "JOIN DISCORD",
    canRetry: true,
  },
  misconfigured: {
    title: "HUB IS TEMPORARILY UNAVAILABLE",
    body: "The Prophets Hub is being configured. Please try again shortly.",
    primary: "JOIN DISCORD",
    canRetry: false,
  },
};

function AuthGate({ state, onClose }) {
  const cfg = HUB_AUTH_GATE_STATES[state];
  const [inviteUrl, setInviteUrl] = useState("https://discord.gg/");
  useEffect(() => {
    // Discord invite URL comes from public landing settings — same
    // source the marketing site uses. Never surface raw Discord API
    // errors here.
    hub.get("/landing/settings")
       .then(r => { if (r.data?.discord_url) setInviteUrl(r.data.discord_url); })
       .catch(() => { /* keep default */ });
  }, []);
  if (!cfg) return null;
  const retryLogin = () => {
    window.location.href = discordLoginUrl();
  };
  return (
    <div className="hub-authgate" role="dialog" aria-modal="true"
         data-testid="hub-authgate"
         data-state={state}>
      <div className="hub-authgate-card">
        <div className="hub-authgate-badge">ACCESS DENIED</div>
        <h2 className="hub-authgate-title" data-testid="hub-authgate-title">{cfg.title}</h2>
        <p className="hub-authgate-body">{cfg.body}</p>
        <div className="hub-authgate-actions">
          <a href={inviteUrl} target="_blank" rel="noreferrer"
             className="hub-btn hub-btn--primary"
             data-testid="hub-authgate-join">
            {cfg.primary} ↗
          </a>
          {cfg.canRetry && (
            <button type="button"
                    className="hub-btn hub-btn--ghost"
                    onClick={retryLogin}
                    data-testid="hub-authgate-retry">
              {cfg.retryLabel || "TRY AGAIN"}
            </button>
          )}
          <button type="button"
                  className="hub-authgate-close"
                  onClick={onClose}
                  data-testid="hub-authgate-close"
                  aria-label="Dismiss">
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HubPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  // Feed state — cursor-based pagination.
  //   feed         : ordered list of currently-displayed cards (chronological, LTR row-by-row via CSS grid)
  //   nextCursor   : opaque server cursor; null when no more pages remain
  //   hasMore      : whether the server has additional pages
  //   loading      : initial-page fetch in flight  ("RECEIVING TRANSMISSIONS…")
  //   loadingMore  : "SHOW MORE" click in flight   ("LOADING MORE…")
  //   errorMore    : last "load more" error message — surfaced so the user can retry without losing already-loaded cards
  //   total        : from the initial page only; used by the stats bar
  const [feed, setFeed] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMore, setErrorMore] = useState("");
  const [total, setTotal] = useState(0);

  const [active, setActive] = useState([]);
  const [platform, setPlatform] = useState(""); const [sort, setSort] = useState("latest");
  const [userFilter, setUserFilter] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [myOpen, setMyOpen] = useState(false);
  // Read OAuth callback state once on mount; strip the query string
  // afterwards so a refresh doesn't re-show the gate.
  const [authGate, setAuthGate] = useState(() => {
    if (typeof window === "undefined") return null;
    const s = new URLSearchParams(window.location.search).get("auth") || "";
    return HUB_AUTH_GATE_STATES[s] ? s : null;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const s = new URLSearchParams(window.location.search).get("auth");
    if (s && (HUB_AUTH_GATE_STATES[s] || s === "ok" || s === "error")) {
      // Clear the ?auth=… param so refreshes / shares don't re-trigger.
      const url = new URL(window.location.href);
      url.searchParams.delete("auth");
      window.history.replaceState({}, "", url.toString());
    }
    // Any successful auth completion (whether the OAuth callback
    // admitted this Discord ID as a HOLDER or as a PROPHETS_FRIEND
    // via the invitation flow) means the pending invite has been
    // consumed — drop it from session so a later logout / re-login
    // starts a clean normal login.
    if (s === "ok") clearPendingInvite();
  }, []);

  const reloadMe = useCallback(() => hub.get("/hub/me").then(r => setMe(r.data)).catch(() => setMe({ authenticated: false })), []);

  useEffect(() => { reloadMe(); }, [reloadMe]);

  // v14.17.16 — centralized session-lost handler. Any widget that
  // uses the shared `lib/api` client and receives a 401 on a
  // `/hub/*` request fires `hub:session-lost` after a single
  // `/hub/me` revalidation confirms the session is truly gone.
  // We react ONCE here (not per widget) by re-hydrating the
  // authoritative `/hub/me` payload, which cascades a clean
  // "authenticated: false" state through every child that reads
  // `me.authenticated`. That collapses the half-authenticated UI
  // (stale header + failing widgets) into a single consistent
  // logged-out state showing CONNECT DISCORD.
  useEffect(() => {
    const onSessionLost = () => { reloadMe(); };
    window.addEventListener("hub:session-lost", onSessionLost);
    return () => window.removeEventListener("hub:session-lost", onSessionLost);
  }, [reloadMe]);

  // Page size for both the initial load and every "Show More" click. Kept
  // small so the first paint is fast and each subsequent click is cheap.
  const PAGE_SIZE = 4;

  // ---- Load the FIRST page for the current filter/sort ----
  // Any filter or sort change resets `feed`, cursor and the "has more"
  // flag before the request goes out — this guarantees the user never
  // sees stale cards from the previous filter while the new page fetches.
  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setErrorMore("");
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    params.set("sort", sort);
    if (userFilter) params.set("user_id", userFilter);
    params.set("limit", String(PAGE_SIZE));
    try {
      const { data } = await hub.get(`/hub/feed?${params.toString()}`);
      setFeed(data.items || []);
      setNextCursor(data.next_cursor || null);
      setHasMore(Boolean(data.has_more));
      setTotal(typeof data.total === "number" ? data.total : 0);
    } finally {
      setLoading(false);
    }
  }, [platform, sort, userFilter]);

  // ---- Append the NEXT page (Show More button) ----
  // Uses the server cursor so no already-loaded card is re-fetched and no
  // card is skipped when two approvals share an identical `approved_at`.
  // Failures preserve the currently-displayed cards and expose `errorMore`
  // so the user can retry without losing their scroll position.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    setErrorMore("");
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    params.set("sort", sort);
    if (userFilter) params.set("user_id", userFilter);
    params.set("limit", String(PAGE_SIZE));
    params.set("cursor", nextCursor);
    try {
      const { data } = await hub.get(`/hub/feed?${params.toString()}`);
      const incoming = data.items || [];
      // Dedupe defensively — realtime refetches could race with a click.
      setFeed(prev => {
        const seen = new Set(prev.map(x => x.submission_id));
        const merged = prev.slice();
        for (const it of incoming) {
          if (!seen.has(it.submission_id)) { seen.add(it.submission_id); merged.push(it); }
        }
        return merged;
      });
      setNextCursor(data.next_cursor || null);
      setHasMore(Boolean(data.has_more));
    } catch (e) {
      setErrorMore("Could not load more transmissions. Try again.");
    } finally {
      setLoadingMore(false);
    }
  }, [platform, sort, userFilter, nextCursor, hasMore, loadingMore]);

  // ---- Silent refresh (realtime + polling) ----
  // Re-fetches the top page(s) and rebuilds `feed` up to the size the user
  // has already loaded (rounded up to a full page). This picks up new
  // approvals / removals without disturbing the user's scroll position or
  // re-fetching pages they never expanded to. Called from `hub:transmission_changed`
  // and the visibility/poll fallbacks — NEVER from the "Show More" click path.
  const refreshLoadedPages = useCallback(async () => {
    const pagesLoaded = Math.max(1, Math.ceil(feed.length / PAGE_SIZE));
    const targetSize = pagesLoaded * PAGE_SIZE;
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    params.set("sort", sort);
    if (userFilter) params.set("user_id", userFilter);
    params.set("limit", String(targetSize));
    try {
      const { data } = await hub.get(`/hub/feed?${params.toString()}`);
      setFeed(data.items || []);
      setNextCursor(data.next_cursor || null);
      setHasMore(Boolean(data.has_more));
      if (typeof data.total === "number") setTotal(data.total);
    } catch (_) { /* silent — keep whatever we already have visible */ }
  }, [platform, sort, userFilter, feed.length]);

  // Reset & fetch page 1 on filter/sort change.
  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  // Active-contributors count powers the "ACTIVE CONTRIBUTORS" stat in
  // the footer bar. The visible "ACTIVE TRANSMISSIONS" avatar panel that
  // previously used this data has been removed from the hero.
  useEffect(() => { hub.get("/hub/active-members").then(r => setActive(r.data.items || [])); }, []);

  // ---- Realtime sync ----
  // `hub:transmission_changed` is PUBLIC now with a minimal payload of
  // `{submission_id, action}` (no user_id, no note text, no prev_status).
  // Any state transition that could affect the public feed re-fetches the
  // already-loaded pages so the user's scroll position is preserved.
  const debouncedRefresh = useDebouncedCallback(() => { refreshLoadedPages(); }, 300);
  useHubEvent("hub:transmission_changed", (msg) => {
    const a = msg?.action;
    if (a === "approved" || a === "removed" || a === "rejected" || a === "pending") {
      debouncedRefresh();
    }
  });
  // Polling fallback (paused when tab is hidden, refetches on focus).
  useHubPoll(refreshLoadedPages, { intervalMs: 15000 });
  useVisibilityRefetch(refreshLoadedPages);

  const openSubmit = () => {
    if (!me?.authenticated) { window.location.href = discordLoginUrl(); return; }
    setSubmitOpen(true);
  };
  const logout = async () => { await hub.post("/hub/auth/logout"); reloadMe(); };

  return (
    <div className="hub-root" data-testid="hub-page">
      {/* First-visit animated onboarding tutorial. Also mounted on
          HubHomePage — either mount is idempotent because the popup
          self-gates on the localStorage flag. */}
      <HubOnboarding />
      <HubHeader me={me}
                 active="transmissions"
                 onMy={() => navigate("/hub/profile?tab=transmissions")}
                 onProfile={() => navigate("/hub/profile")}
                 onLogout={logout} />

      <div className="hub-shell">
        <section className="hub-hero">
          <div className="hub-hero-left">
            <h1 className="hub-hero-title">COMMUNITY TRANSMISSIONS</h1>
            <p className="hub-hero-sub">Share a link to your X, Instagram, or TikTok post about Prophets and earn 5 Fragments once approved. Support other community posts for a chance to unlock surprise rewards. (Max 1 submission per day)</p>
            <div className="hub-hero-actions">
              <button className="hub-btn hub-btn--primary" onClick={openSubmit} data-testid="hub-submit-cta">
                SUBMIT A TRANSMISSION →
              </button>
            </div>
          </div>
        </section>

        <div className="hub-controls">
          <div className="hub-filters" role="tablist">
            {PLATFORMS.map((p) => (
              <button key={p.id || "all"} className={`hub-filter ${platform === p.id ? "is-active" : ""}`}
                      onClick={() => setPlatform(p.id)} data-testid={`hub-filter-${p.id || "all"}`}>
                {p.label}
              </button>
            ))}
            {userFilter && (
              <button className="hub-filter hub-filter--user" onClick={() => setUserFilter("")}
                      data-testid="hub-filter-user-clear">✕ member filter</button>
            )}
          </div>
          <div className="hub-sort">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="hub-sort">
              <option value="latest">LATEST</option>
              <option value="oldest">OLDEST</option>
            </select>
          </div>
        </div>

        <div className="hub-feed" data-testid="hub-feed">
          {loading && feed.length === 0 ? (
            <div className="hub-empty hub-empty--loading" data-testid="hub-feed-initial-loading">
              <span className="hub-embed-loading-dot" />
              <span>RECEIVING TRANSMISSIONS…</span>
            </div>
          ) : feed.length === 0 ? (
            <div className="hub-empty">No transmissions yet. Be the first to submit.</div>
          ) : feed.map((it) => <PostCard key={it.submission_id} item={it} />)}
        </div>

        {/* Show More button — only rendered when the current filter has
            additional pages. Disabled state doubles as the "LOADING MORE…"
            status while a request is in flight. When exhausted, we
            render a subtle "ALL TRANSMISSIONS LOADED" pill instead. */}
        {!loading && feed.length > 0 && (
          <div className="hub-showmore-row" data-testid="hub-showmore-row">
            {hasMore ? (
              <button type="button"
                      className="hub-btn hub-btn--primary hub-showmore-btn"
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="hub-showmore-btn">
                {loadingMore ? "LOADING MORE…" : "SHOW MORE TRANSMISSIONS"}
              </button>
            ) : (
              <div className="hub-showmore-done" data-testid="hub-showmore-done">
                ALL TRANSMISSIONS LOADED
              </div>
            )}
            {errorMore && (
              <div className="hub-showmore-err" data-testid="hub-showmore-err">{errorMore}</div>
            )}
          </div>
        )}

        <div className="hub-stats-bar">
          <span>TOTAL TRANSMISSIONS <b>{total.toLocaleString()}</b></span>
          <span>· ACTIVE CONTRIBUTORS <b>{active.length}</b></span>
        </div>
      </div>

      <SubmitModal open={submitOpen} onClose={() => setSubmitOpen(false)} onSubmitted={() => { loadFirstPage(); }} />
      <MyTransmissions open={myOpen} onClose={() => setMyOpen(false)} />
      {authGate && <AuthGate state={authGate} onClose={() => setAuthGate(null)} />}
    </div>
  );
}
